import {
  ExponentVenue,
  FogoLendStrategy,
  HypurrCardOperationType,
  HypurrEulerNetPositionsStrategy,
  HypurrNetPoolPositionsStrategy,
  LiquidityV3Strategy,
  MoneyMarketPositionType,
  PyronStrategy,
  StripePaymentEventType,
} from '../domain';

export enum UserIdentifierProperty {
  UserAddress = 'address',
  GithubUsername = 'github_username',
  TwitterUsername = 'twitter_username',
  DiscordUsername = 'discord_username',
}

export enum TriggerType {
  Custom = 'custom',
  LiquidityPool = 'liquidity_pool',
  SushiSwapTrade = 'sushiswap-trade',
  NadoTrade = 'nado-trade',
  NadoPurpsTrade = 'nado-purps-trade',
  DuneQuery = 'dune-query',
  PendleLPToken = 'pendle-lp-token',
  ZealyQuest = 'zealy-quest',
  GalxeQuest = 'galxe-quest',
  LiquidityPoolV2 = 'liquidity-pool-v2',
  Balancer = 'balancer',
  TokenHolder = 'token-holder',
  SuiTokenHolder = 'sui-token-holder',
  CompoundV3 = 'compound-v3',
  MorphoBorrowerStrategy = 'morpho-borrower-strategy',
  HypurrNetPoolPositions = 'hypurr-net-pool-positions',
  HypurrEulerNetPositions = 'hypurr-euler-net-positions',
  HyperswapLiquidityV3 = 'hyperswap-liquidity-v3',
  HybraLiquidityV3 = 'hybra-liquidity-v3',
  UniswapLiquidityV3 = 'uniswap-liquidity-v3',
  UniswapLiquidityV4 = 'uniswap-liquidity-v4',
  PrjXLiquidityV3 = 'prjx-liquidity-v3',
  KittenSwapLiquidityV3 = 'kittenswap-liquidity-v3',
  UltrasolidLiquidityV3 = 'ultrasolid-liquidity-v3',
  UpheavalLiquidityV3 = 'upheaval-liquidity-v3',
  HibachiTrading = 'hibachi-trading',
  HibachiAffiliateReferredFees = 'hibachi-affiliate-referred-fees',
  OrderlyBuilderTrades = 'orderly',
  TwitterVerifyPost = 'twitter-verify-post',
  TwitterVerifyRetweet = 'twitter-verify-retweet',
  // Reserved for the gated "Like on X" coming-soon path; matches fuul-server's contract.
  TwitterVerifyLike = 'twitter-verify-like',
  HyperliquidTrade = 'hyperliquid-trade',
  SolanaTokenHolder = 'solana-token-holder',
  LynxTrade = 'lynx-trade',
  SubgraphBalance = 'subgraph-balance',
  FollowOnX = 'follow-on-x',
  PendleYT = 'pendle-yt',
  AmbientTrade = 'ambient-trade',
  ValiantTrade = 'valiant-trade',
  ValiantLiquidity = 'valiant-liquidity',
  Pyron = 'pyron',
  FogoLend = 'fogolend',
  FelixVanillaCollateral = 'felix-vanilla-collateral',
  FelixCdpCollateral = 'felix-cdp-collateral',
  FogoTokenHolder = 'fogo-token-holder',
  StellarTokenHolder = 'stellar-token-holder',
  BlendPositions = 'blend-positions',
  AquariusLiquidity = 'aquarius-liquidity',
  AquariusSwaps = 'aquarius-swaps',
  HypurrCardTransactions = 'hypurr-card-transactions',
  NestLiquidityV3 = 'nest-liquidity-v3',
  FogoIgnitionLocked = 'fogo-ignition-locked',
  PolymarketTradeVolume = 'polymarket-trade-volume',
  Gearbox = 'gearbox',
  PacificaBuilderTrades = 'pacifica-builder-trades',
  OdysseyBuilderTrades = 'odyssey-builder-trades',
  MorphoVaults = 'morpho-vaults',
  EulerV2Looping = 'euler-v2-looping',
  EulerVaults = 'euler-vaults',
  SpectraYT = 'spectra-yt',
  SpectraLP = 'spectra-lp',
  DiscordServerJoin = 'discord-server-join',
  DiscordRoleAssigned = 'discord-role-assigned',
  StripePayment = 'stripe-payment',
  Exponent = 'exponent',

  /** @deprecated Use 'custom' instead */
  Classic = 'classic',
  //
  /** @deprecated */
  VertexTrade = 'vertex-trade',
  //
}

export interface LiquidLocker {
  address: string;
  receiptToken: string;
}

export enum OrderlyVolumeType {
  AllVolume = 'perpVolume',
  TakerVolume = 'perpTakerVolume',
  MakerVolume = 'perpMakerVolume',
}

export enum AmbientVolumeType {
  MakerVolume = 'maker_volume_24h',
  TakerVolume = 'taker_volume_24h',
  AllVolume = 'volume_24h',
}

export interface LiquidLockerDTO {
  address: string;
  receipt_token: string;
}

export enum GearboxMode {
  TotalPosition = 'total_position',
  Supply = 'supply',
}

export enum MaxLeverageOperator {
  LessThan = 'less_than',
  LessThanOrEqual = 'less_than_or_equal',
}

export const GEARBOX_MODE_LABELS: Record<string, string> = {
  [GearboxMode.TotalPosition]: 'Total Position',
  [GearboxMode.Supply]: 'Supply',
  'total-position': 'Total Position',
};

export type TriggerContext = {
  chainId?: number;
  chain_id?: number;
  name?: string;
  description?: string;
  tokenFilter?: object;
  duneQueryId?: number;
  currencyAddress?: string;
  ref?: string;
  sy_address?: string;
  yt_address?: string;
  lp_address?: string;
  price_reference_address?: string;
  liquid_lockers?: LiquidLockerDTO[];
  contracts?: { chain_id: number; address: string }[];
  subdomain?: string;
  quest_id?: string;
  token_address?: string;
  volume_currency_expression?: string;
  market_address?: string;
  market_chain_id?: number;
  position_type?: string;
  type?: string;
  native_currency_address?: string;
  native_currency_decimals?: number;
  liquidityPoolId?: string;
  strategy?:
    | LiquidityV3Strategy
    | MoneyMarketPositionType
    | HypurrNetPoolPositionsStrategy
    | HypurrEulerNetPositionsStrategy
    | PyronStrategy
    | FogoLendStrategy;
  distributionFormulaArgs?: {
    liquidityShareWeight: number;
    token0ShareWeight: number;
    token1ShareWeight: number;
    inRangeLiquidity?: boolean;
  };
  activeLiquidityArgs?: {
    tokenDenominator?: string;
  };
  pairAddress?: string;
  hyTokenAddress?: string;
  variableDebtTokenAddress?: string;
  depositTokenAddress?: string;
  debtTokenAddress?: string;
  volumeCurrencyAddress?: string;
  brokerId?: string;
  volumeType?: OrderlyVolumeType | AmbientVolumeType;
  text?: string;
  builderAddress?: string;
  chain_identifier?: string;
  referredFeesLevel?: HibachiAffiliateReferredFeesLevel;
  revenueType?: OrderlyRevenueType;
  poolAddress?: string;
  condition_expression?: string;
  subgraphName?: string;
  tokenCurrency?: string;
  usernameToFollow?: string;
  postId?: string;
  ytAddress?: string;
  syAddress?: string;
  network?: string;
  poolAddresses?: string[];
  asset?: string;
  market?: string;
  pool_address?: string;
  /** Uniswap V4: a bytes32 pool id, since a V4 pool has no contract address of its own. */
  pool_id?: string;
  token_denominator?: string;
  marketId?: string;
  tokenAddress?: string;
  operationType?: HypurrCardOperationType;
  chainIdentifier?: number;
  conditionId?: string;
  gearboxMode?: GearboxMode;
  mode?: string;
  asset_address?: string;
  builderCode?: string;
  vault?: string;
  underlying_address?: string;
  depositVault?: string;
  borrowVault?: string;
  max_leverage?: string | number;
  max_leverage_operator?: string;
  guild_id?: string;
  role_id?: string;
  paymentEventType?: StripePaymentEventType;
  // Exponent preset. `vault_address` is snake_case on the wire and is a base58 Solana
  // address that must never be lowercased; do not reuse the EVM `vault` key above.
  vault_address?: string;
  venue?: ExponentVenue;
};

export type HibachiTradingTriggerContext = {
  chainId: number;
};

export type OrderlyBuilderTradesTriggerContext = {
  brokerId: string;
  volumeType: OrderlyVolumeType;
};

export enum OrderlyRevenueType {
  TotalFee = 'totalFee',
  BrokerFee = 'brokerFee',
  RealizedPnl = 'realizedPnl',
}

export enum HibachiAffiliateReferredFeesLevel {
  Level1 = 'level1',
  Level2 = 'level2',
  Level3 = 'level3',
}

export type HibachiAffiliateReferredFeesTriggerContext = {
  chainId: number;
  referredFeesLevel: HibachiAffiliateReferredFeesLevel;
};

export type FelixVanillaCollateralTriggerContext = {
  marketId: string;
  asset: string;
  chainId: number;
  condition_expression?: string;
};

export type FelixCdpCollateralTriggerContext = {
  token_address: string;
  chainId: number;
  condition_expression?: string;
};

export type HypurrCardTransactionsTriggerContext = {
  operationType: HypurrCardOperationType;
  chainId: number;
};

export type PolymarketTradeVolumeTriggerContext = {
  conditionId: string;
};

export type StripePaymentTriggerContext = {
  paymentEventType: StripePaymentEventType;
};
